/**
 * Partial chart buckets (CHART-01…05): a bucket is partial when it ends after
 * `toDate` (the current day/week/month) or starts before `fromDate` (a period
 * that starts in the middle of a week/month). Detected client-side from the
 * filter's range and granularity; the API marks nothing.
 */
import { DateTime } from 'luxon';
import type { Locales, TranslationFunctions } from '@/i18n/i18n-types';
import type { ChartGranularity } from '@/lib/chart-axis';
import { getLuxonLocale } from '@/lib/utils';

export interface PartialEdges {
  first: boolean;
  last: boolean;
}

export interface ChartRange {
  from: string;
  to: string;
}

/**
 * Last instant of the bucket that contains `startIso` (same week start as
 * `bucketStartIso`).
 */
export function bucketEnd(
  startIso: string,
  granularity: ChartGranularity
): DateTime {
  return DateTime.fromISO(startIso).endOf(granularity);
}

/** Whether the first/last bucket of a chart is partial (CHART-01). */
export function partialEdges(
  bucketStarts: string[],
  range: ChartRange,
  granularity: ChartGranularity
): PartialEdges {
  if (bucketStarts.length === 0) return { first: false, last: false };
  const from = DateTime.fromISO(range.from);
  const to = DateTime.fromISO(range.to);
  const firstStart = DateTime.fromISO(bucketStarts[0]).startOf(granularity);
  const lastEnd = bucketEnd(bucketStarts[bucketStarts.length - 1], granularity);
  return { first: firstStart < from, last: lastEnd > to };
}

export type PartialRow<T, K extends string> = Omit<T, K> &
  Record<K, number | null> &
  Record<`${K}Partial`, number | null> & { partial: boolean };

/**
 * Splits `valueKey` in two series for the area chart (CHART-02): the solid
 * one (`valueKey`) loses the partial buckets, and `${valueKey}Partial` holds
 * only each partial bucket and its neighbour, so the dashed segment starts
 * where the solid line stops (no gap). `partial` flags the partial buckets.
 */
export function markPartialRows<
  T extends Record<K, number | null>,
  K extends string,
>(rows: T[], valueKey: K, edges: PartialEdges): PartialRow<T, K>[] {
  const lastIndex = rows.length - 1;
  const isPartial = (i: number) =>
    (edges.first && i === 0) || (edges.last && i === lastIndex);
  const isDashed = (i: number) =>
    (edges.first && i <= 1) || (edges.last && i >= lastIndex - 1);

  return rows.map((row, i) => {
    const value = row[valueKey];
    const marked = {
      ...row,
      [valueKey]: isPartial(i) ? null : value,
      [`${valueKey}Partial`]: isDashed(i) ? value : null,
      partial: isPartial(i),
    };
    // Computed keys widen to `string`; the two keys above are exactly
    // `K` and `${K}Partial`.
    return marked as unknown as PartialRow<T, K>;
  });
}

/** "19 out" (pt) / "Oct 19" (en). */
export function formatDayMonth(date: DateTime, locale: Locales): string {
  const month = date
    .setLocale(getLuxonLocale(locale))
    .toFormat('MMM')
    .replace(/\.$/, '');
  return locale === 'en' ? `${month} ${date.day}` : `${date.day} ${month}`;
}

/**
 * Label next to the partial point/bar (CHART-03). The last bucket reads as
 * the current hour/day/week/month when `toDate` is now (same hour for hourly
 * charts, same day otherwise); else "Parcial, até {d} {mmm}".
 */
// SPEC_DEVIATION: takes `locale` (design: `partialLabel(edge, granularity,
// range, now, LL)`). Reason: "{d} {mmm}" is formatted per locale.
export function partialLabel(
  edge: 'first' | 'last',
  granularity: ChartGranularity,
  range: ChartRange,
  now: DateTime,
  LL: TranslationFunctions,
  locale: Locales
): string {
  if (edge === 'first') {
    return LL.chartPartial.since({
      date: formatDayMonth(DateTime.fromISO(range.from), locale),
    });
  }

  const to = DateTime.fromISO(range.to);
  const sameUnit = granularity === 'hour' ? 'hour' : 'day';
  if (to.hasSame(now, sameUnit)) {
    const time = to.toFormat('HH:mm');
    switch (granularity) {
      case 'hour':
        return LL.chartPartial.currentHour({ time });
      case 'day':
        return LL.chartPartial.today({ time });
      case 'week':
        return LL.chartPartial.currentWeek();
      case 'month':
        return LL.chartPartial.currentMonth();
      case 'minute':
        // Spec defines no "current minute" label; falls through to the
        // dated one below.
        break;
    }
  }
  return LL.chartPartial.until({ date: formatDayMonth(to, locale) });
}

/** Tooltip/headline/readout label of a bucket, "· parcial" when partial (CHART-05). */
export function partialReadoutLabel(
  label: string,
  partial: boolean,
  LL: TranslationFunctions
): string {
  return partial ? LL.chartPartial.readout({ label }) : label;
}
